<template>
  <section class="profile-screen">
    <div class="profile-shell">
      <button class="profile-back" @click="$emit('close')">返回首页</button>

      <div v-if="view === 'profile'" class="profile-layout">
        <aside class="profile-summary">
          <span class="eyebrow">User Center</span>
          <h2>个人中心设置</h2>
          <p>管理你的探索者名称，或进入独立的密码安全界面。</p>
          <div class="profile-badge">
            <strong>{{ profileForm.name || displayName || '探索者' }}</strong>
            <span>{{ profileForm.username || 'Backrooms Operator' }}</span>
          </div>
        </aside>

        <section class="profile-panel">
          <div class="block-head">
            <span class="eyebrow">Profile</span>
            <strong>基础资料</strong>
          </div>
          <el-form :model="profileForm" label-position="top" class="profile-form">
            <el-form-item label="账号" v-if="profileForm.username">
              <el-input v-model="profileForm.username" disabled />
            </el-form-item>
            <el-form-item label="名称">
              <el-input v-model="profileForm.name" maxlength="10" placeholder="设置展示名称" show-word-limit />
            </el-form-item>
            <div class="profile-actions">
              <el-button type="primary" :loading="profileSaving" @click="saveProfile">保存名称</el-button>
              <el-button class="profile-link-btn" @click="view = 'password'">修改密码</el-button>
            </div>
          </el-form>
        </section>
      </div>

      <div v-else class="profile-layout password-layout">
        <aside class="profile-summary">
          <span class="eyebrow">Security</span>
          <h2>修改密码</h2>
          <p>这是独立的密码安全界面。完成修改后，可返回个人中心继续设置资料。</p>
        </aside>

        <section class="profile-panel">
          <div class="block-head">
            <span class="eyebrow">Password</span>
            <strong>密码安全</strong>
          </div>
          <el-form :model="passwordForm" label-position="top" class="profile-form">
            <el-form-item label="旧密码">
              <el-input v-model="passwordForm.old" type="password" maxlength="20" show-password />
            </el-form-item>
            <el-form-item label="新密码">
              <el-input v-model="passwordForm.new" type="password" maxlength="20" show-password />
            </el-form-item>
            <el-form-item label="确认新密码">
              <el-input v-model="passwordForm.new1" type="password" maxlength="20" show-password />
            </el-form-item>
            <div class="profile-actions">
              <el-button type="primary" :loading="passwordSaving" @click="savePassword">保存密码</el-button>
              <el-button class="profile-link-btn" @click="view = 'profile'">返回个人中心</el-button>
            </div>
          </el-form>
        </section>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { ajax } from '@/api';
import { userData } from '@/store/userData';

defineProps<{
  displayName: string;
}>();

defineEmits<{
  (e: 'close'): void;
}>();

const useData = userData();
const view = ref<'profile' | 'password'>('profile');
const profileSaving = ref(false);
const passwordSaving = ref(false);
const profileForm = reactive({
  username: '',
  name: ''
});
const passwordForm = reactive({
  old: '',
  new: '',
  new1: ''
});

const loadProfile = () => {
  profileForm.name = useData.name || sessionStorage.getItem('name') || '';
  ajax({
    url: 'user/findMe',
    method: 'POST',
    isLoading: false,
    success: (res: any) => {
      if (res.code !== 0) return;
      const data = res.data || {};
      profileForm.username = data.username || '';
      profileForm.name = data.name || profileForm.name;
    }
  });
};

const saveProfile = () => {
  const name = profileForm.name.trim();
  if (!name) {
    ElMessage.warning('请输入名称');
    return;
  }
  profileSaving.value = true;
  ajax({
    url: 'user/user-info/update',
    method: 'POST',
    data: { name },
    success: (res: any) => {
      profileSaving.value = false;
      if (res.code === 0) {
        useData.setName(name);
        ElMessage.success(res.msg || '名称已更新');
      } else {
        ElMessage.error(res.msg);
      }
    },
    error: () => {
      profileSaving.value = false;
      ElMessage.error('保存失败，请稍后重试');
    }
  });
};

const savePassword = () => {
  if (!passwordForm.old || !passwordForm.new || !passwordForm.new1) {
    ElMessage.warning('请完整填写密码');
    return;
  }
  if (passwordForm.new.length < 8 || passwordForm.new.length > 20) {
    ElMessage.warning('新密码长度必须为8-20位');
    return;
  }
  if (passwordForm.new !== passwordForm.new1) {
    ElMessage.error('两次输入的密码不一致');
    return;
  }
  passwordSaving.value = true;
  ajax({
    url: 'user/password/update',
    method: 'POST',
    data: passwordForm,
    success: (res: any) => {
      passwordSaving.value = false;
      if (res.code === 0) {
        passwordForm.old = '';
        passwordForm.new = '';
        passwordForm.new1 = '';
        ElMessage.success(res.msg || '密码已更新');
        view.value = 'profile';
      } else {
        ElMessage.error(res.msg);
      }
    },
    error: () => {
      passwordSaving.value = false;
      ElMessage.error('密码修改失败，请稍后重试');
    }
  });
};

onMounted(loadProfile);
</script>
